import { SerializedProject, SerializedLayer } from './types';

/**
 * @file project/serializer.ts
 * @description Provides pure functions for project serialization and data string conversion.
 * Strictly follows the no-shared-global-state architecture.
 */

/**
 * @function serializeProject
 * @description Converts the current project components into a JSON-serialized string.
 * This function is pure and relies strictly on its input parameters.
 * @param {number} width - The image width in pixels.
 * @param {number} height - The image height in pixels.
 * @param {string} backgroundColor - The background hex or CSS color.
 * @param {any[]} layers - Array of layer objects containing canvas and metadata.
 * @returns {Promise<string>} A JSON string representing the serialized project.
 */
export async function serializeProject(
  width: number,
  height: number,
  backgroundColor: string,
  layers: any[]
): Promise<string> {
  const project: SerializedProject = {
    version: 2,
    width: width,
    height: height,
    backgroundColor: backgroundColor,
    layers: [],
  };

  // [CRITICAL_LOGIC_START]
  for (const layer of layers) {
    let dataUrl: string;
    const canvas: any = layer.canvas;

    if (typeof canvas.toDataURL === 'function') {
      dataUrl = canvas.toDataURL('image/png');
    } else {
      // Buffer/OffscreenCanvas fallback
      const tempCanvas: HTMLCanvasElement = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const context: CanvasRenderingContext2D | null = tempCanvas.getContext('2d');
      if (!context) {
        throw new Error('[Serializer] Failed to get 2D context for serialization buffer.');
      }
      context.drawImage(canvas, 0, 0);
      dataUrl = tempCanvas.toDataURL('image/png');
    }

    project.layers.push({
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
      locked: layer.locked,
      type: layer.type,
      data: dataUrl,
      shapes: layer.shapes,
      textData: layer.textData,
    });
  }
  // [CRITICAL_LOGIC_END]

  return JSON.stringify(project);
}

/**
 * @function parseProjectData
 * @description Decodes a project JSON string into a structured SerializedProject object.
 * Performs validation but does not apply changes to the application global state.
 * @param {string} jsonString - The project data string.
 * @returns {SerializedProject} The validated project structure.
 * @throws {Error} If the project string is malformed or invalid.
 */
export function parseProjectData(jsonString: string): SerializedProject {
  try {
    const project: SerializedProject = JSON.parse(jsonString) as SerializedProject;
    
    if (!project.width || !project.height || !project.layers) {
      throw new Error('[Serializer] Invalid project file format: Missing required dimensions or layers.');
    }

    console.log(`[Serializer] Project structure parsed (v${project.version || 1}).`);
    return project;
  } catch (error) {
    console.error('[Serializer] Failed to parse project JSON:', error);
    throw error;
  }
}
